import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppDatePicker } from '../../shared/components/AppDatePicker';
import { ApiError } from '../../api/httpClient';
import { getPartnerSubmissions, getReturnRequestDetail } from '../../api/vipMpApi';
import { useVipMpSession } from '../../context/VipMpSessionContext';
import { PartnerSubmissionDetail } from './PartnerSubmissionDetail';
import { TableSkeleton } from '../../shared/components/LoadingSkeleton';
import {
  formatDate,
  formatMoney,
  partnerFacingStatus,
  pick,
  pickNumber,
  resolveRequestNumber,
  resolveReturnRequestId,
  statusClassName
} from './partnerData';

export type PartnerMySubmissionsTabProps = {
  onStartUpdate?: (ctx: { returnRequestId: number; requestNumber: string }) => void;
  hideCardHeader?: boolean;
};

export function PartnerMySubmissionsTab({ onStartUpdate, hideCardHeader }: PartnerMySubmissionsTabProps) {
  const session = useVipMpSession();
  const emailToQuery = (session.partnerContactEmail || session.adobeUser?.email || '').trim();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof getReturnRequestDetail>> | null>(null);

  const load = useCallback(async () => {
    if (!emailToQuery) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const data = await getPartnerSubmissions({ partnerEmail: emailToQuery });
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      setErr(error instanceof ApiError ? error.message : 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [emailToQuery]);

  useEffect(() => {
    void load();
  }, [load]);

  // Deep link from email: /partner/submissions?case=123 → open case, keeping the URL param while viewing
  useEffect(() => {
    const caseParam = searchParams.get('case');
    if (!caseParam) return;
    const id = parseInt(caseParam, 10);
    if (!Number.isFinite(id)) return;
    void openDetail(id, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally runs once on mount only

  // Browser back: ?case= removed from URL → close detail
  useEffect(() => {
    if (!searchParams.get('case') && detailId !== null) {
      setDetailId(null);
      setDetail(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const availableStatuses = [
    { label: 'New',                    value: 'new' },
    { label: 'In Progress',            value: 'in progress' },
    { label: 'Approved',               value: 'approved' },
    { label: 'Rejected',               value: 'rejected' },
    { label: 'Pending Partner Action', value: 'pending partner action' },
  ];

  const filtered = useMemo(() => {
    return rows.filter((sub) => {
      const requestNumber = resolveRequestNumber(sub).toLowerCase();
      const rawId = pick(sub, ['ReturnRequestId', 'returnRequestId', 'Return_Request_ID']).toLowerCase();
      const orderNumber = pick(sub, ['Adobe_Sales_Order_Number', 'AdobeSalesOrderNumber']).toLowerCase();
      const customer = pick(sub, ['Customer_Name', 'CustomerName']).toLowerCase();
      const status = partnerFacingStatus(sub).toLowerCase();
      const submitted = pick(sub, ['SubmittedUtc', 'submittedUtc', 'EntryDateTime', 'CreatedUtc', 'createdUtc']);
      const submittedDate = submitted ? new Date(String(submitted)) : null;
      const q = search.trim().toLowerCase();

      if (q && ![requestNumber, rawId, orderNumber, customer].some((value) => value.includes(q))) return false;
      if (statusFilter && status !== statusFilter.toLowerCase()) return false;
      if (dateFrom) {
        const from = new Date(`${dateFrom}T00:00:00`);
        if (!submittedDate || submittedDate < from) return false;
      }
      if (dateTo) {
        const to = new Date(`${dateTo}T23:59:59`);
        if (!submittedDate || submittedDate > to) return false;
      }
      return true;
    });
  }, [dateFrom, dateTo, rows, search, statusFilter]);

  const openDetail = async (returnRequestId: number, replaceHistory = false) => {
    setDetailId(returnRequestId);
    setErr(null);
    setSearchParams({ case: String(returnRequestId) }, { replace: replaceHistory });
    try {
      const result = await getReturnRequestDetail(returnRequestId);
      setDetail(result);
    } catch (error) {
      setDetailId(null);
      setDetail(null);
      setSearchParams({}, { replace: replaceHistory });
      setErr(error instanceof ApiError ? error.message : 'Failed to open case');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
  };

  if (detailId != null && detail) {
    return (
      <PartnerSubmissionDetail
        detail={detail}
        returnRequestId={detailId}
        onBack={() => {
          setDetailId(null);
          setDetail(null);
          setSearchParams({});
        }}
        onStartUpdate={onStartUpdate}
      />
    );
  }

  return (
    <div>
      <div className="filters-bar">
        <div className="filters-search-row">
          <div className="search-box">
            <span className="search-icon">/</span>
            <input
              type="search"
              placeholder="Search by Order #, Request ID, or Customer name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="filters-dropdowns-row">
          <div className="filter-group">
            <label htmlFor="partner-status-filter">Status</label>
            <select
              id="partner-status-filter"
              className="filter-control"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              {availableStatuses.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="partner-date-from">From</label>
            <AppDatePicker
              id="partner-date-from"
              value={dateFrom}
              max={dateTo || undefined}
              className="filter-control"
              onChange={(val) => { setDateFrom(val); if (dateTo && val && val > dateTo) setDateTo(''); }}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="partner-date-to">To</label>
            <AppDatePicker
              id="partner-date-to"
              value={dateTo}
              min={dateFrom || undefined}
              className="filter-control"
              onChange={(val) => { setDateTo(val); if (dateFrom && val && val < dateFrom) setDateFrom(''); }}
            />
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void load()}>
            Refresh
          </button>
          <button type="button" className="clear-filters" onClick={clearFilters}>
            Clear filters
          </button>
        </div>
      </div>

      {loading && <TableSkeleton rows={6} cols={7} />}
      {err && (
        <div className="card">
          <div className="card-body" style={{ color: '#c62828' }}>
            {err}
          </div>
        </div>
      )}

      {!loading && !err && (
        <div className="card">
          {!hideCardHeader && (
            <div className="card-header">
              <h2>Submissions</h2>
            </div>
          )}
          <div className="card-body">
            <div className="recent-cases-scroll-wrapper" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Request ID</th>
                    <th>Order Number</th>
                    <th>Customer</th>
                    <th>Return Sum</th>
                    <th>Fee</th>
                    <th>Submitted</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((sub) => {
                    const rrId = resolveReturnRequestId(sub);
                    const req = resolveRequestNumber(sub);
                    const status = partnerFacingStatus(sub);
                    const currency =
                      pick(sub, ['RequestCurrencyCode', 'requestCurrencyCode', 'CurrencyCode', 'currencyCode']) || 'USD';
                    const returnSum = pickNumber(sub, ['ReturnAmount', 'returnAmount', 'ReturnSum', 'returnSum']);
                    const feeAmount = pickNumber(sub, ['FeeAmount', 'feeAmount']);
                    return (
                      <tr
                        key={req || String(rrId)}
                        onClick={() => {
                          if (!Number.isNaN(rrId)) {
                            void openDetail(rrId);
                          }
                        }}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && !Number.isNaN(rrId)) {
                            e.preventDefault();
                            void openDetail(rrId);
                          }
                        }}
                        role={!Number.isNaN(rrId) ? 'button' : undefined}
                        tabIndex={!Number.isNaN(rrId) ? 0 : undefined}
                      >
                        <td>{req || `#${rrId}`}</td>
                        <td>{pick(sub, ['Adobe_Sales_Order_Number', 'AdobeSalesOrderNumber']) || '-'}</td>
                        <td>{pick(sub, ['Customer_Name', 'CustomerName']) || '-'}</td>
                        <td>{formatMoney(returnSum, currency)}</td>
                        <td>{formatMoney(feeAmount, currency)}</td>
                        <td>{formatDate(pick(sub, ['SubmittedUtc', 'submittedUtc', 'EntryDateTime', 'CreatedUtc', 'createdUtc']))}</td>
                        <td>
                          <span className={`status-badge ${statusClassName(status)}`}>{status}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="pagination-info" style={{ marginTop: 10 }}>
              Showing {filtered.length} of {rows.length} submission{rows.length === 1 ? '' : 's'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
